import React, { createContext, useContext, useState, useCallback } from 'react';
import apiService from '../services/api';

const TreeContext = createContext(null);
export const useTree = () => {
  const ctx = useContext(TreeContext);
  if (!ctx) throw new Error('useTree must be used within TreeProvider');
  return ctx;
};

const ROLE_LEVELS = { viewer: 1, editor: 2, 'co-founder': 3, founder: 4 };
export const hasRole = (userRole, minRole) =>
  (ROLE_LEVELS[userRole] || 0) >= (ROLE_LEVELS[minRole] || 0);

export const TreeProvider = ({ children }) => {
  const [currentTree, setCurrentTree] = useState(null);
  const [persons, setPersons] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [trees, setTrees] = useState([]);
  const [archivedTrees, setArchivedTrees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const fetchTrees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getTrees();
      setTrees(res.trees || []);
      setArchivedTrees(res.archivedTrees || []);
    } catch (err) { console.error('Fetch trees:', err); }
    setLoading(false);
  }, []);

  const fetchTree = useCallback(async (treeId) => {
    setLoading(true);
    try {
      const { tree, persons: p, relationships: r } = await apiService.getTree(treeId);
      setCurrentTree(tree);
      setPersons(p || []);
      setRelationships(r || []);
      // Sync role from trees list (server sends userRole on GET /api/trees)
      setTrees(prev => {
        const match = prev.find(t => t._id?.toString() === treeId?.toString());
        if (match?.userRole) setUserRole(match.userRole);
        return prev;
      });
    } catch (err) { console.error('Fetch tree:', err); }
    setLoading(false);
  }, []);

  const createTree = useCallback(async (name, description) => {
    const { tree } = await apiService.createTree({ name, description });
    setTrees(prev => [...prev, tree]);
    return tree;
  }, []);

  const addPerson = useCallback(async (data) => {
    const result = await apiService.addPerson(data);
    const person = result.person;
    if (result.inviteCode) person._inviteCode = result.inviteCode;
    setPersons(prev => [...prev, person]);
    return person;
  }, []);

  const updatePerson = useCallback(async (id, updates) => {
    const { person } = await apiService.updatePerson(id, updates);
    setPersons(prev => prev.map(p => p._id === id ? person : p));
    return person;
  }, []);

  const transitionToMemorial = useCallback(async (id, data) => {
    const { person } = await apiService.transitionToMemorial(id, data);
    setPersons(prev => prev.map(p => p._id === id ? person : p));
    return person;
  }, []);

  const revertMemorial = useCallback(async (id) => {
    const { person } = await apiService.revertMemorial(id);
    setPersons(prev => prev.map(p => p._id === id ? person : p));
    return person;
  }, []);

  const addMilestone = useCallback(async (id, milestone) => {
    const { person } = await apiService.addMilestone(id, milestone);
    setPersons(prev => prev.map(p => p._id === id ? person : p));
    return person;
  }, []);

  return (
    <TreeContext.Provider value={{
      currentTree, persons, relationships, trees, archivedTrees, loading,
      userRole, setUserRole,
      fetchTrees, fetchTree, createTree, addPerson,
      updatePerson, transitionToMemorial, revertMemorial, addMilestone,
      setCurrentTree,
    }}>
      {children}
    </TreeContext.Provider>
  );
};
